import { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  MapPin, 
  Navigation, 
  Phone, 
  MessageCircle, 
  User, 
  Users, 
  Briefcase, 
  Clock, 
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Car,
  XCircle,
  Camera,
  Upload,
  X,
  AlertTriangle,
  Smartphone,
  RotateCcw,
  MapPinned,
  Locate,
  Shield,
  Settings,
  Star,
  Target,
  Sparkles,
  TrendingUp,
  Plane,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import useGPSTracking from '../hooks/useGPSTracking';
import ActionStatusOverlay from '../components/shared/ActionStatusOverlay';


// Google Maps API Key - should be in env
const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';

export default function DriverRideView() {
  const { rideId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [ride, setRide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInstructions, setShowInstructions] = useState(false);
  const [ridePhase, setRidePhase] = useState('departing'); // departing, navigating, in_progress, completed, no_show
  const [successOverlay, setSuccessOverlay] = useState(null);
  
  // Check if location restrictions are enabled for this user (default to true if not set)
  const isLocationRestricted = user?.location_restricted !== false;
  
  // No Show Modal State
  const [showNoShowModal, setShowNoShowModal] = useState(false);
  const [noShowPhotos, setNoShowPhotos] = useState([]); // Array of {data, filename, preview}
  const [noShowNotes, setNoShowNotes] = useState('');
  const [submittingNoShow, setSubmittingNoShow] = useState(false);
  
  // Ride Rating State
  const [rideRating, setRideRating] = useState(null);
  const [loadingRating, setLoadingRating] = useState(false);
  
  // Flight Delay Modal State
  const [showFlightDelayModal, setShowFlightDelayModal] = useState(false);
  const [flightDelayTime, setFlightDelayTime] = useState('');
  const [flightDelayProof, setFlightDelayProof] = useState(null);
  const [flightDelayNotes, setFlightDelayNotes] = useState('');
  const [submittingFlightDelay, setSubmittingFlightDelay] = useState(false);

  // Nameboard Mode State
  const [isLandscape, setIsLandscape] = useState(false);
  const [nameboardEnabled, setNameboardEnabled] = useState(false);
  
  // GPS Tracking State
  const [showGeofenceWarning, setShowGeofenceWarning] = useState(false);
  const [geofenceWarningMessage, setGeofenceWarningMessage] = useState('');
  const [pendingAction, setPendingAction] = useState(null); // 'arrived-pickup' or 'arrived-dropoff'
  const [isRequestingGPS, setIsRequestingGPS] = useState(false);
  // Anti-double-tap lock for ride-action buttons. Prevents the frontend from firing
  // the same mutation twice when the driver double-taps on slow networks (which used
  // to create duplicate financial entries before the backend upsert fix).
  const [actionInFlight, setActionInFlight] = useState(null); // 'pickup' | 'picked-up' | 'dropoff' | null
  
  // Dropoff reminder state
  const [showDropoffReminder, setShowDropoffReminder] = useState(false);
  const [wasNearDropoff, setWasNearDropoff] = useState(false);
  const [dropoffReminderShown, setDropoffReminderShown] = useState(false);
  
  // Initialize GPS tracking with faster updates
  const {
    currentLocation,
    locationError,
    isTracking,
    permissionStatus,
    startTracking,
    getCurrentPosition,
    isWithinGeofence,
    requestPermission,
    calculateDistance,
    GEOFENCE_RADIUS,
    PICKUP_HARD_BLOCK_RADIUS,
    DROPOFF_REMINDER_RADIUS
  } = useGPSTracking(token, { enabled: true, rideId, updateInterval: 5000 });

  // Check if GPS permission is granted - MANDATORY for ride access
  const isGPSPermissionGranted = permissionStatus === 'granted';
  const isGPSPermissionDenied = permissionStatus === 'denied';
  const isGPSPermissionPending = permissionStatus === 'prompt' || permissionStatus === 'unknown';
  const isGPSPermissionChecking = permissionStatus === 'checking';

  // Handle GPS permission request
  const handleRequestGPSPermission = async () => {
    setIsRequestingGPS(true);
    try {
      await requestPermission();
    } catch (error) {
      console.error('GPS permission request failed:', error);
      toast.error('Failed to request GPS permission');
    } finally {
      setIsRequestingGPS(false);
    }
  };

  // Open browser/device settings for GPS - Show clear instructions modal
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const [detectedPlatform, setDetectedPlatform] = useState('desktop');

  // Detect platform on mount
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) {
      setDetectedPlatform('ios');
    } else if (userAgent.includes('android')) {
      setDetectedPlatform('android');
    } else {
      setDetectedPlatform('desktop');
    }
  }, []);

  // Try to request permission again - this is the most reliable method
  const handleRetryPermission = async () => {
    setIsRequestingGPS(true);
    try {
      // This will show the browser's native permission prompt if not permanently denied
      const result = await requestPermission();
      if (result === 'granted') {
        toast.success('GPS enabled successfully!');
        // Reload to refresh the view
        window.location.reload();
      } else if (result === 'denied') {
        // Permission is permanently denied, show instructions
        setShowInstructionsModal(true);
      } else {
        toast.error('Please allow GPS access when prompted');
      }
    } catch (error) {
      console.error('Permission request error:', error);
      setShowInstructionsModal(true);
    } finally {
      setIsRequestingGPS(false);
    }
  };

  // Open instructions modal
  const handleOpenSettings = () => {
    setShowInstructionsModal(true);
  };

  // Request location permission on mount (but don't auto-request if denied)
  useEffect(() => {
    if (permissionStatus === 'prompt') {
      // Auto-trigger permission request for 'prompt' state
      handleRequestGPSPermission();
    }
  }, [permissionStatus]);

  // Detect orientation changes for Nameboard Mode
  useEffect(() => {
    const checkOrientation = () => {
      const isLandscapeMode = window.innerWidth > window.innerHeight;
      setIsLandscape(isLandscapeMode);
    };

    // Initial check
    checkOrientation();

    // Listen for resize events (covers orientation change)
    window.addEventListener('resize', checkOrientation);
    
    // Also listen for orientation change event (mobile specific)
    window.addEventListener('orientationchange', () => {
      // Small delay to let the browser update dimensions
      setTimeout(checkOrientation, 100);
    });

    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  // Enable nameboard mode when driver arrives at pickup (but hasn't picked up customer yet)
  useEffect(() => {
    if (ride && ride.arrived_at_pickup && !ride.picked_up_customer && ride.status !== 'no_show' && ride.status !== 'completed') {
      setNameboardEnabled(true);
    } else {
      setNameboardEnabled(false);
    }
  }, [ride]);

  useEffect(() => {
    fetchRide();
  }, [rideId]);

  useEffect(() => {
    if (ride) {
      // Determine phase based on ride status and sub-status
      if (ride.status === 'no_show') {
        setRidePhase('no_show');
      } else if (ride.status === 'completed') {
        setRidePhase('completed');
      } else if (ride.arrived_at_dropoff) {
        setRidePhase('completed');
      } else if (ride.picked_up_customer) {
        setRidePhase('in_progress');
      } else if (ride.arrived_at_pickup) {
        setRidePhase('navigating');
      } else if (ride.status === 'in_progress' || ride.navigation_started_at) {
        setRidePhase('navigating');
      } else {
        setRidePhase('departing');
      }
    }
  }, [ride]);

  // Monitor dropoff proximity - trigger reminder if driver passes dropoff and moves away (1km)
  useEffect(() => {
    // Only monitor when ride is in "picked up customer" phase (customer in vehicle)
    if (!ride?.picked_up_customer || ride?.arrived_at_dropoff || ride?.status === 'completed' || ride?.status === 'no_show') {
      return;
    }
    
    // Need dropoff coordinates and current location
    if (!currentLocation || !ride?.dropoff_lat || !ride?.dropoff_lng) {
      return;
    }
    
    const distanceToDropoff = calculateDistance(
      currentLocation.latitude,
      currentLocation.longitude,
      ride.dropoff_lat,
      ride.dropoff_lng
    );
    
    // Check if driver is near dropoff (within 500m)
    if (distanceToDropoff <= 500) {
      setWasNearDropoff(true);
    }
    
    // If driver was near dropoff and now moved more than 1km away, trigger reminder
    if (wasNearDropoff && distanceToDropoff > DROPOFF_REMINDER_RADIUS && !dropoffReminderShown) {
      // Trigger phone vibration - strong pattern for attention
      if ('vibrate' in navigator) {
        navigator.vibrate([500, 200, 500, 200, 500, 200, 500]); // Strong vibrate pattern
      }
      
      // Show reminder popup
      setShowDropoffReminder(true);
      setDropoffReminderShown(true);
      
      // Also show a toast notification for visibility
      toast.error(
        `Did you forget to complete the dropoff? You are ${(distanceToDropoff / 1000).toFixed(1)}km away!`,
        { duration: 10000 }
      );
      
      // Play alert sound if available
      try {
        const audio = new Audio('/alert.mp3');
        audio.play().catch(() => {}); // Ignore if audio fails
      } catch (e) { console.warn('Alert sound unavailable:', e); }
    }
  }, [currentLocation, ride, wasNearDropoff, dropoffReminderShown, calculateDistance, DROPOFF_REMINDER_RADIUS]);

  // Reset dropoff reminder state when ride changes
  useEffect(() => {
    setWasNearDropoff(false);
    setDropoffReminderShown(false);
    setShowDropoffReminder(false);
  }, [rideId]);

  // Calculate if driver is within pickup geofence for button HARD BLOCKING
  // Accounts for GPS accuracy to prevent false blocks
  // If location_restricted is false, driver can proceed regardless of location
  const pickupGeofenceStatus = (() => {
    // If user is not location restricted, always allow
    if (!isLocationRestricted) {
      return { canProceed: true, distance: null, message: null, isHardBlocked: false, isWithinSoftGeofence: true, accuracy: null };
    }
    if (!currentLocation || !ride?.pickup_lat || !ride?.pickup_lng) {
      return { canProceed: true, distance: null, message: null, isHardBlocked: false, accuracy: null };
    }
    const distance = calculateDistance(
      currentLocation.latitude,
      currentLocation.longitude,
      ride.pickup_lat,
      ride.pickup_lng
    );
    
    // Account for GPS accuracy - add accuracy buffer to tolerance
    const gpsAccuracy = currentLocation.accuracy || 0;
    const accuracyBuffer = Math.max(gpsAccuracy, 100); // Minimum 100m buffer
    const effectiveHardBlockRadius = PICKUP_HARD_BLOCK_RADIUS + accuracyBuffer;
    const effectiveSoftRadius = GEOFENCE_RADIUS + accuracyBuffer;
    
    const isWithinHardBlock = distance <= effectiveHardBlockRadius;
    const isWithinSoft = distance <= effectiveSoftRadius;
    
    return {
      canProceed: isWithinHardBlock,
      isHardBlocked: !isWithinHardBlock,
      isWithinSoftGeofence: isWithinSoft,
      distance: Math.round(distance),
      accuracy: Math.round(gpsAccuracy),
      message: !isWithinHardBlock 
        ? `You can't select Pick up because you are too far from the pickup location. You are ${(distance / 1000).toFixed(1)}km away. (GPS accuracy: ±${Math.round(gpsAccuracy)}m)`
        : (!isWithinSoft ? `Note: You are ${Math.round(distance)}m away from the pickup location. (GPS accuracy: ±${Math.round(gpsAccuracy)}m)` : null)
    };
  })();

  // Calculate if driver is within dropoff geofence for button blocking
  // Accounts for GPS accuracy to prevent false blocks
  // If location_restricted is false, driver can proceed regardless of location
  const dropoffGeofenceStatus = (() => {
    // If user is not location restricted, always allow
    if (!isLocationRestricted) {
      return { canProceed: true, distance: null, message: null, accuracy: null };
    }
    if (!currentLocation || !ride?.dropoff_lat || !ride?.dropoff_lng) {
      return { canProceed: true, distance: null, message: null, accuracy: null };
    }
    const distance = calculateDistance(
      currentLocation.latitude,
      currentLocation.longitude,
      ride.dropoff_lat,
      ride.dropoff_lng
    );
    
    // Account for GPS accuracy - add accuracy buffer to tolerance
    const gpsAccuracy = currentLocation.accuracy || 0;
    const accuracyBuffer = Math.max(gpsAccuracy, 100); // Minimum 100m buffer
    const effectiveRadius = GEOFENCE_RADIUS + accuracyBuffer;
    
    const isWithin = distance <= effectiveRadius;
    return {
      canProceed: isWithin,
      distance: Math.round(distance),
      accuracy: Math.round(gpsAccuracy),
      message: isWithin ? null : `You can't complete the ride because you are ${Math.round(distance)}m away from the dropoff location. You need to be within ${Math.round(effectiveRadius)}m. (GPS accuracy: ±${Math.round(gpsAccuracy)}m)`
    };
  })();

  const fetchRide = async () => {
    try {
      const response = await axios.get(`${API}/rides/${rideId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRide(response.data);
      
      // Load rating if ride is completed or no_show
      if (response.data.status === 'completed' || response.data.status === 'no_show') {
        fetchRideRating();
      }
    } catch (error) {
      console.error('Error fetching ride:', error);
      toast.error('Failed to load ride details');
    } finally {
      setLoading(false);
    }
  };

  // Fetch ride rating
  const fetchRideRating = async () => {
    setLoadingRating(true);
    try {
      const response = await axios.get(`${API}/rides/${rideId}/rating`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRideRating(response.data);
    } catch (error) {
      console.error('Error fetching ride rating:', error);
      // Rating might not exist yet or be calculating
    } finally {
      setLoadingRating(false);
    }
  };

  const startNavigation = async () => {
    try {
      let body = {};
      if (ride?.source === 'transferz') {
        let loc = currentLocation;
        if (!loc) {
          try { loc = await getCurrentPosition(); } catch (e) { console.warn('No position for Transferz status:', e); }
        }
        if (loc) body = { latitude: loc.latitude, longitude: loc.longitude };
      }
      await axios.put(`${API}/rides/${rideId}/start-navigation`, body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRidePhase('navigating');
      // Start GPS tracking when navigating
      startTracking();
      fetchRide();
    } catch (error) {
      toast.error('Failed to start navigation');
    }
  };

  // Check geofence and handle "arrived at pickup" with location validation - HARD BLOCK at 2km
  const arrivedAtPickup = async () => {
    if (actionInFlight) return;
    // HARD BLOCK: Cannot proceed if more than 2km away
    if (pickupGeofenceStatus.isHardBlocked) {
      toast.error(pickupGeofenceStatus.message);
      // Trigger phone vibration for feedback
      if ('vibrate' in navigator) {
        navigator.vibrate([200, 100, 200]);
      }
      return;
    }
    
    // Show warning if outside soft geofence (1km) but allow to proceed
    if (!pickupGeofenceStatus.isWithinSoftGeofence && pickupGeofenceStatus.distance) {
      toast.warning(`Note: You are ${pickupGeofenceStatus.distance}m away from pickup location.`);
    }
    
    setActionInFlight('pickup');
    try {
      // Get current location
      let location = currentLocation;
      if (!location) {
        try {
          location = await getCurrentPosition();
        } catch (e) {
          console.warn('Could not get current position:', e);
        }
      }
      
      // Use location-aware endpoint if we have location
      if (location) {
        const response = await axios.put(
          `${API}/rides/${rideId}/arrived-at-pickup-with-location`,
          { latitude: location.latitude, longitude: location.longitude },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.geofence_warning) {
          toast.warning(response.data.geofence_warning);
        }
      } else {
        // Fallback to non-location endpoint
        await axios.put(`${API}/rides/${rideId}/arrived-at-pickup`, {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      setSuccessOverlay('arrived_pickup');
      fetchRide();
    } catch (error) {
      toast.error(error.response?.data?.detail || (error.response ? `Failed to update (HTTP ${error.response.status})` : 'Server unreachable - check your connection'));
    } finally {
      setActionInFlight(null);
    }
  };

  // Pick up customer - HARD BLOCK at 2km, warning between 1-2km
  const pickedUpCustomer = async () => {
    if (actionInFlight) return;
    // HARD BLOCK: Cannot proceed if more than 2km away
    if (pickupGeofenceStatus.isHardBlocked) {
      toast.error(pickupGeofenceStatus.message);
      // Trigger phone vibration for feedback
      if ('vibrate' in navigator) {
        navigator.vibrate([200, 100, 200]);
      }
      return;
    }
    
    // Show warning if outside soft geofence (1km) but allow to proceed
    if (!pickupGeofenceStatus.isWithinSoftGeofence && pickupGeofenceStatus.distance) {
      toast.warning(`Note: You are ${pickupGeofenceStatus.distance}m away from pickup location.`);
    }
    
    setActionInFlight('picked-up');
    try {
      let body = {};
      if (ride?.source === 'transferz') {
        let loc = currentLocation;
        if (!loc) {
          try { loc = await getCurrentPosition(); } catch (e) { console.warn('No position for Transferz status:', e); }
        }
        if (loc) body = { latitude: loc.latitude, longitude: loc.longitude };
      }
      await axios.put(`${API}/rides/${rideId}/picked-up-customer`, body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRidePhase('in_progress');
      setSuccessOverlay('picked_up');
      fetchRide();
    } catch (error) {
      toast.error(error.response?.data?.detail || (error.response ? `Failed to update (HTTP ${error.response.status})` : 'Server unreachable - check your connection'));
    } finally {
      setActionInFlight(null);
    }
  };

  // Check geofence and handle "arrived at dropoff" with location validation - keep as warning (allow proceed)
  const completeRide = async (forceSubmit = false) => {
    if (actionInFlight) return;
    setActionInFlight('dropoff');
    try {
      // Get current location
      let location = currentLocation;
      if (!location) {
        try {
          location = await getCurrentPosition();
        } catch (e) {
          console.warn('Could not get current position:', e);
        }
      }
      
      // Check geofence if ride has coordinates and we have location
      if (!forceSubmit && location && ride?.dropoff_lat && ride?.dropoff_lng) {
        const geofenceCheck = isWithinGeofence(ride.dropoff_lat, ride.dropoff_lng);
        if (!geofenceCheck.within) {
          setGeofenceWarningMessage(
            `You are ${geofenceCheck.distance}m away from the dropoff location. ` +
            `You should be within ${GEOFENCE_RADIUS}m to mark completion.`
          );
          setPendingAction('arrived-dropoff');
          setShowGeofenceWarning(true);
          // Release the lock so the user can confirm via the modal afterwards.
          setActionInFlight(null);
          return;
        }
      }
      
      // Use location-aware endpoint if we have location
      if (location) {
        const response = await axios.put(
          `${API}/rides/${rideId}/arrived-at-dropoff-with-location`,
          { latitude: location.latitude, longitude: location.longitude },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.geofence_warning) {
          toast.warning(response.data.geofence_warning);
        }
      } else {
        // Fallback to non-location endpoint
        await axios.put(`${API}/rides/${rideId}/arrived-at-dropoff`, {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      setSuccessOverlay('completed');
      setRidePhase('completed');
      fetchRide();
    } catch (error) {
      toast.error('Failed to complete ride');
    } finally {
      setActionInFlight(null);
    }
  };

  // Handle geofence warning confirmation (proceed anyway)
  const handleGeofenceConfirm = async () => {
    setShowGeofenceWarning(false);
    if (pendingAction === 'arrived-pickup') {
      await arrivedAtPickup(true);
    } else if (pendingAction === 'arrived-dropoff') {
      await completeRide(true);
    }
    setPendingAction(null);
  };

  // No Show Handlers
  // Compress image before upload to avoid payload/MongoDB size limits
  const isHeicFile = (file) => {
    const name = (file.name || '').toLowerCase();
    return file.type === 'image/heic' || file.type === 'image/heif' || name.endsWith('.heic') || name.endsWith('.heif');
  };

  const browserLikelySupportsHeic = () => {
    const ua = navigator.userAgent;
    return /safari/i.test(ua) && !/chrome|chromium|crios|fxios|android|edg/i.test(ua);
  };

  const compressImage = (file, maxWidth = 1200, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      let settled = false;
      const timer = setTimeout(() => fail(new Error('image-processing-timeout')), 15000);
      const fail = (err) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(err instanceof Error ? err : new Error('image-processing-failed'));
      };
      const reader = new FileReader();
      reader.onerror = () => fail(new Error('file-read-failed'));
      reader.onload = (e) => {
        const img = new Image();
        img.onerror = () => fail(new Error('image-decode-failed'));
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            const compressed = canvas.toDataURL('image/jpeg', quality);
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            resolve(compressed);
          } catch (err) {
            fail(err);
          }
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleNoShowProofSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Check total count (max 15 photos)
    if (noShowPhotos.length + files.length > 15) {
      toast.error('Maximum 15 photos allowed');
      return;
    }
    
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File "${file.name}" is too large. Maximum size is 10MB.`);
        continue;
      }
      
      if (isHeicFile(file) && !browserLikelySupportsHeic()) {
        toast.error(`"${file.name}" is a HEIC photo not supported by this browser. Please take a new photo or select a JPEG/PNG file.`);
        continue;
      }
      
      let compressed;
      try {
        compressed = await compressImage(file);
      } catch (err) {
        console.error(`Photo processing failed for ${file.name}:`, err);
        toast.error(`We couldn't process "${file.name}". Please take a new photo or select a different one.`);
        continue;
      }
      setNoShowPhotos(prev => [...prev, {
        id: Date.now() + Math.random(),
        data: compressed,
        filename: file.name.replace(/\.\w+$/, '.jpg'),
        preview: compressed
      }]);
    }
    
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const removeNoShowPhoto = (photoId) => {
    setNoShowPhotos(prev => prev.filter(p => p.id !== photoId));
  };

  const handleSubmitNoShow = async () => {
    if (noShowPhotos.length === 0) {
      toast.error('Please upload at least one proof photo');
      return;
    }
    
    setSubmittingNoShow(true);
    try {
      // Step 1: Mark ride as no-show first (without photos to keep payload small)
      await axios.put(`${API}/rides/${rideId}/no-show`, {
        proof_photos: [],
        notes: noShowNotes
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });
      
      // Step 2: Upload each photo individually via multipart (bypasses proxy size limits)
      let uploadedCount = 0;
      for (const photo of noShowPhotos) {
        try {
          // Convert base64 to blob for multipart upload
          const base64Data = photo.data.split(',')[1] || photo.data;
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'image/jpeg' });
          
          const formData = new FormData();
          formData.append('file', blob, photo.filename);
          
          await axios.post(`${API}/rides/${rideId}/no-show-photo-upload`, formData, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            },
            timeout: 30000
          });
          uploadedCount++;
        } catch (photoErr) {
          console.error(`Error uploading photo ${photo.filename}:`, photoErr);
        }
      }
      
      if (uploadedCount === noShowPhotos.length) {
        toast.success(`Ride marked as No Show (${uploadedCount} photos uploaded)`);
      } else if (uploadedCount > 0) {
        toast.warning(`Ride marked as No Show (${uploadedCount}/${noShowPhotos.length} photos uploaded)`);
      } else {
        toast.warning('Ride marked as No Show but photos failed to upload. Please retry adding photos.');
      }
      
      setShowNoShowModal(false);
      setRidePhase('no_show');
      fetchRide();
    } catch (error) {
      console.error('Error marking no show:', error);
      if (error.response?.status === 413) {
        toast.error('Photos are too large. Try uploading fewer or smaller photos.');
      } else if (error.code === 'ECONNABORTED') {
        toast.error('Upload timed out. Try uploading fewer photos.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to mark as no show. Check your connection and try again.');
      }
    } finally {
      setSubmittingNoShow(false);
    }
  };

  const resetNoShowModal = () => {
    setShowNoShowModal(false);
    setNoShowPhotos([]);
    setNoShowNotes('');
  };

  // Flight Delay Handlers
  const handleFlightDelayProofSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }
    if (isHeicFile(file) && !browserLikelySupportsHeic()) {
      toast.error(`"${file.name}" is a HEIC photo not supported by this browser. Please take a new photo or select a JPEG/PNG file.`);
      e.target.value = '';
      return;
    }
    let compressed;
    try {
      compressed = await compressImage(file);
    } catch (err) {
      console.error(`Photo processing failed for ${file.name}:`, err);
      toast.error(`We couldn't process "${file.name}". Please take a new photo or select a different one.`);
      e.target.value = '';
      return;
    }
    setFlightDelayProof({ data: compressed, filename: file.name.replace(/\.\w+$/, '.jpg'), preview: compressed });
    e.target.value = '';
  };

  const handleSubmitFlightDelay = async () => {
    if (!flightDelayTime) {
      toast.error('Please enter the new arrival time');
      return;
    }
    setSubmittingFlightDelay(true);
    try {
      // Build ISO datetime from the time input + ride date
      const rideDate = ride.date_time ? new Date(ride.date_time) : new Date();
      const [hours, minutes] = flightDelayTime.split(':');
      const newArrival = new Date(rideDate);
      newArrival.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      // If new arrival is before original, assume next day
      if (newArrival < rideDate) {
        newArrival.setDate(newArrival.getDate() + 1);
      }

      const payload = {
        new_arrival_time: newArrival.toISOString(),
        notes: flightDelayNotes || null,
      };
      if (flightDelayProof) {
        payload.proof_data = flightDelayProof.data;
        payload.proof_filename = flightDelayProof.filename;
      }
      await axios.post(`${API}/rides/${rideId}/flight-delay`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Flight delay reported successfully');
      setShowFlightDelayModal(false);
      setFlightDelayTime('');
      setFlightDelayProof(null);
      setFlightDelayNotes('');
      fetchRide();
    } catch (error) {
      console.error('Error reporting flight delay:', error);
      toast.error(error.response?.data?.detail || 'Failed to report flight delay');
    } finally {
      setSubmittingFlightDelay(false);
    }
  };

  const resetFlightDelayModal = () => {
    setShowFlightDelayModal(false);
    setFlightDelayTime('');
    setFlightDelayProof(null);
    setFlightDelayNotes('');
  };

  const openGoogleMaps = (destination) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
    window.open(url, '_blank');
  };

  const callPassenger = () => {
    if (ride?.customer_phone) {
      window.location.href = `tel:${ride.customer_phone}`;
    } else {
      toast.error('No phone number available');
    }
  };

  const messagePassenger = () => {
    if (ride?.customer_phone) {
      window.location.href = `sms:${ride.customer_phone}`;
    } else {
      toast.error('No phone number available');
    }
  };

  const whatsappPassenger = () => {
    if (ride?.customer_phone) {
      const cleaned = ride.customer_phone.replace(/[\s\-()]/g, '').replace(/^\+/, '');
      window.open(`https://wa.me/${cleaned}`, '_blank');
    } else {
      toast.error('No phone number available');
    }
  };

  const getMapUrl = () => {
    if (!ride) return '';
    const origin = encodeURIComponent(ride.pickup_location || '');
    const destination = encodeURIComponent(ride.dropoff_location || '');
    
    if (ridePhase === 'in_progress') {
      // Show route to dropoff
      return `https://www.google.com/maps/embed/v1/directions?key=${GOOGLE_MAPS_KEY}&origin=${origin}&destination=${destination}&mode=driving`;
    }
    // Show route to pickup
    return `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_KEY}&q=${origin}`;
  };

  const getStaticMapUrl = () => {
    if (!ride) return '';
    const pickup = encodeURIComponent(ride.pickup_location || 'Athens');
    const dropoff = encodeURIComponent(ride.dropoff_location || 'Athens');
    
    // Use a static map image as fallback
    return `https://maps.googleapis.com/maps/api/staticmap?size=600x400&markers=color:green|label:P|${pickup}&markers=color:red|label:D|${dropoff}&key=${GOOGLE_MAPS_KEY}`;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '--:--';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-4 border-amber-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Show loading while checking GPS permission
  // Skip GPS permission check if still checking (show loading)
  if (isGPSPermissionChecking && isLocationRestricted) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center">
        <div className="animate-spin h-12 w-12 border-4 border-amber-500 border-t-transparent rounded-full mb-4"></div>
        <p className="text-white text-sm">Checking GPS permission...</p>
      </div>
    );
  }

  // MANDATORY GPS PERMISSION CHECK - Block access if GPS is not granted
  // EXCEPTION: If user has location_restricted=false, bypass this check entirely
  if (!isGPSPermissionGranted && isLocationRestricted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex flex-col" data-testid="gps-required-screen">
        {/* Header */}
        <div className="p-4">
          <button onClick={() => navigate('/driver')} className="text-white/80 hover:text-white">
            <ArrowLeft className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-white">
          <div className="w-24 h-24 bg-amber-500 rounded-full flex items-center justify-center mb-6 shadow-2xl">
            <MapPin className="h-14 w-14 text-white" />
          </div>
          
          <h1 className="text-3xl font-bold mb-2 text-center">GPS Required</h1>
          <p className="text-slate-300 mb-8 text-center max-w-sm">
            Location access is mandatory to view and manage rides. This ensures accurate tracking and verification.
          </p>

          {/* Benefits */}
          <div className="w-full max-w-sm bg-white/10 backdrop-blur-sm rounded-2xl p-6 space-y-4 mb-8">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Navigation className="h-5 w-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-white font-medium text-sm">Accurate Tracking</p>
                <p className="text-slate-400 text-xs">Verify arrivals at pickup and dropoff</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <MapPinned className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <p className="text-white font-medium text-sm">Geofence Validation</p>
                <p className="text-slate-400 text-xs">Confirm you're at the right location</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-slate-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Shield className="h-5 w-5 text-slate-400" />
              </div>
              <div>
                <p className="text-white font-medium text-sm">Privacy Protected</p>
                <p className="text-slate-400 text-xs">Location used only during active rides</p>
              </div>
            </div>
          </div>

          {/* Status Message */}
          {isGPSPermissionDenied && (
            <div className="w-full max-w-sm bg-red-500/20 border border-red-500/30 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-300 font-medium text-sm">Permission Denied</p>
                  <p className="text-red-300/70 text-xs">
                    GPS access was blocked. Please enable it in your browser/device settings to continue.
                  </p>
                </div>
              </div>
            </div>
          )}

          {isGPSPermissionPending && (
            <div className="w-full max-w-sm bg-amber-500/20 border border-amber-500/30 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-2">
                <Locate className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-300 font-medium text-sm">Permission Required</p>
                  <p className="text-amber-300/70 text-xs">
                    Click the button below to allow GPS access.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bottom Buttons */}
        <div className="p-6 space-y-3">
          {isGPSPermissionDenied ? (
            <>
              {/* Primary: Show how to enable GPS */}
              <Button
                onClick={handleOpenSettings}
                className="w-full h-14 bg-amber-500 hover:bg-amber-600 text-white rounded-2xl text-lg font-semibold shadow-lg"
              >
                <Settings className="h-5 w-5 mr-2" />
                How to Enable GPS
              </Button>
              
              {/* Secondary: Try again after user enables */}
              <Button
                onClick={handleRetryPermission}
                disabled={isRequestingGPS}
                variant="outline"
                className="w-full h-12 border-2 border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 rounded-2xl font-medium"
              >
                {isRequestingGPS ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin h-4 w-4 border-2 border-emerald-400 border-t-transparent rounded-full"></div>
                    Checking...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <RotateCcw className="h-4 w-4" />
                    I've Enabled GPS - Try Again
                  </span>
                )}
              </Button>
            </>
          ) : (
            <Button
              onClick={handleRequestGPSPermission}
              disabled={isRequestingGPS}
              className="w-full h-14 bg-emerald-500 hover:bg-emerald-600 text-white rounded-2xl text-lg font-semibold shadow-lg shadow-emerald-500/30"
            >
              {isRequestingGPS ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                  Requesting...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Enable GPS Location
                </span>
              )}
            </Button>
          )}
          
          <Button
            onClick={() => navigate('/driver')}
            variant="ghost"
            className="w-full h-12 text-slate-400 hover:text-white hover:bg-white/5 rounded-2xl"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
        
        {/* GPS Instructions Modal - inside GPS Required screen */}
        <Dialog open={showInstructionsModal} onOpenChange={setShowInstructionsModal}>
          <DialogContent className="sm:max-w-md mx-4 max-h-[85vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-slate-800">
                <Settings className="h-5 w-5 text-amber-500" />
                Enable GPS Location
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-2">
              {/* Platform-specific instructions */}
              {detectedPlatform === 'ios' && (
                <div className="space-y-4">
                  <div className="bg-slate-100 rounded-lg p-3">
                    <p className="text-sm font-medium text-slate-700 mb-1">Detected: iPhone/iPad</p>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
                      <div>
                        <p className="font-medium text-slate-800">Open Settings app</p>
                        <p className="text-sm text-slate-600">Go to your iPhone Settings</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
                      <div>
                        <p className="font-medium text-slate-800">Privacy & Security → Location Services</p>
                        <p className="text-sm text-slate-600">Turn ON Location Services</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
                      <div>
                        <p className="font-medium text-slate-800">Find Safari (or your browser)</p>
                        <p className="text-sm text-slate-600">Set to "While Using the App"</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center font-bold text-sm">4</div>
                      <div>
                        <p className="font-medium text-slate-800">Return here and tap "Try Again"</p>
                        <p className="text-sm text-slate-600">The app will request permission again</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {detectedPlatform === 'android' && (
                <div className="space-y-4">
                  <div className="bg-slate-100 rounded-lg p-3">
                    <p className="text-sm font-medium text-slate-700 mb-1">Detected: Android</p>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
                      <div>
                        <p className="font-medium text-slate-800">Open Settings app</p>
                        <p className="text-sm text-slate-600">Go to your phone Settings</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
                      <div>
                        <p className="font-medium text-slate-800">Location</p>
                        <p className="text-sm text-slate-600">Turn ON "Use location"</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
                      <div>
                        <p className="font-medium text-slate-800">In Chrome: tap ⋮ → Settings → Site settings → Location</p>
                        <p className="text-sm text-slate-600">Make sure Location is set to "Ask first" or "Allowed"</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center font-bold text-sm">4</div>
                      <div>
                        <p className="font-medium text-slate-800">Return here and tap "Try Again"</p>
                        <p className="text-sm text-slate-600">The app will request permission again</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {detectedPlatform === 'desktop' && (
                <div className="space-y-4">
                  <div className="bg-slate-100 rounded-lg p-3">
                    <p className="text-sm font-medium text-slate-700 mb-1">Desktop Browser</p>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
                      <div>
                        <p className="font-medium text-slate-800">Click the lock icon 🔒 in address bar</p>
                        <p className="text-sm text-slate-600">It's at the left side of the URL</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
                      <div>
                        <p className="font-medium text-slate-800">Find "Location" in permissions</p>
                        <p className="text-sm text-slate-600">Change from "Block" to "Allow"</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
                      <div>
                        <p className="font-medium text-slate-800">Click "Try Again" below</p>
                        <p className="text-sm text-slate-600">The app will request permission again</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="pt-2 space-y-2">
                <Button
                  onClick={() => {
                    setShowInstructionsModal(false);
                    handleRetryPermission();
                  }}
                  className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  I've Done It - Try Again
                </Button>
                <Button
                  onClick={() => setShowInstructionsModal(false)}
                  variant="outline"
                  className="w-full h-10 rounded-xl"
                >
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  if (!ride) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center text-white">
        <p>Ride not found</p>
        <Button onClick={() => navigate('/driver')} className="mt-4">
          Back to Dashboard
        </Button>
      </div>
    );
  }

  // NAMEBOARD MODE - Full screen passenger name display for airport pickups
  // Activates when phone is in landscape AND driver has arrived at pickup
  if (isLandscape && nameboardEnabled) {
    return (
      <div 
        className="fixed inset-0 bg-black flex flex-col items-center justify-center z-50"
        style={{ 
          width: '100vw', 
          height: '100vh',
          touchAction: 'none' // Prevent accidental scrolling
        }}
        data-testid="nameboard-mode"
      >
        {/* Passenger Name - Maximum visibility */}
        <div className="flex-1 flex items-center justify-center w-full px-8">
          <h1 
            className="text-white font-bold text-center uppercase tracking-wide"
            style={{
              fontSize: 'clamp(3rem, 15vw, 12rem)',
              lineHeight: 1.1,
              textShadow: '0 0 30px rgba(255,255,255,0.1)',
              wordBreak: 'break-word',
              maxWidth: '90vw'
            }}
            data-testid="nameboard-passenger-name"
          >
            {ride.customer_name || 'PASSENGER'}
          </h1>
        </div>

        {/* Subtle branding at bottom */}
        <div className="pb-6">
          <p 
            className="text-white/30 text-sm tracking-[0.3em] uppercase"
            style={{ fontSize: 'clamp(0.6rem, 1.5vw, 0.875rem)' }}
          >
            Outsiders Travel
          </p>
        </div>

        {/* Hidden instruction - appears briefly on tap */}
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
          <p className="text-white/10 text-xs">
            Rotate to portrait to exit
          </p>
        </div>
      </div>
    );
  }

  // COMPLETED SCREEN
  if (ridePhase === 'completed') {
    // Helper to extract score from rating breakdown (handles both formats)
    const getScoreValue = (val) => {
      if (val === null || val === undefined) return null;
      if (typeof val === 'object') return val.score;
      return val;
    };

    // Get performance level
    const getPerformanceLevel = (score) => {
      if (score === null || score === undefined) return null;
      if (score >= 95) return { name: 'Elite', color: 'bg-amber-500' };
      if (score >= 90) return { name: 'Pro', color: 'bg-emerald-500' };
      if (score >= 80) return { name: 'Good', color: 'bg-sky-500' };
      if (score >= 70) return { name: 'Standard', color: 'bg-slate-400' };
      return { name: 'Low', color: 'bg-red-500' };
    };

    const ratingScore = rideRating?.total_score;
    const level = getPerformanceLevel(ratingScore);

    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-600 to-emerald-800 flex flex-col">
        <ActionStatusOverlay action={successOverlay} onDone={() => setSuccessOverlay(null)} />
        {/* Header */}
        <div className="p-4">
          <button onClick={() => navigate('/driver')} className="text-white/80 hover:text-white">
            <ArrowLeft className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col items-center px-6 text-white overflow-y-auto pb-4">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mb-4 shadow-2xl">
            <CheckCircle className="h-12 w-12 text-emerald-600" />
          </div>
          
          <h1 className="text-2xl font-bold mb-1">Ride Completed!</h1>
          <p className="text-emerald-100 text-sm mb-4">Great job!</p>

          {/* Ride Rating Section */}
          {loadingRating ? (
            <div className="w-full max-w-sm bg-white/10 backdrop-blur-sm rounded-2xl p-4 mb-4 text-center">
              <p className="text-emerald-200 text-sm">Loading rating...</p>
            </div>
          ) : rideRating ? (
            <div className="w-full max-w-sm bg-white/15 backdrop-blur-sm rounded-2xl p-4 mb-4">
              {/* Score Header */}
              <div className="text-center mb-4">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Star className="h-5 w-5 text-amber-400" />
                  <span className="text-emerald-200 text-xs uppercase tracking-wide">Ride Score</span>
                </div>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold text-white">{ratingScore?.toFixed(0) || '0'}</span>
                  <span className="text-lg text-emerald-200">/ 100</span>
                </div>
                {level && (
                  <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold ${level.color} text-white`}>
                    {level.name}
                  </span>
                )}
              </div>

              {/* Score Breakdown */}
              <div className="grid grid-cols-2 gap-2">
                {/* Punctuality */}
                <div className="bg-white/10 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Clock className="h-3.5 w-3.5 text-sky-400" />
                    <span className="text-[10px] text-emerald-200 uppercase">Punctuality</span>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-xl font-bold text-white">
                      {(() => {
                        const score = getScoreValue(rideRating.punctuality);
                        return score !== null ? Math.round(score) : 'N/A';
                      })()}
                    </span>
                    {getScoreValue(rideRating.punctuality) !== null && 
                      <span className="text-xs text-emerald-300">pts</span>}
                  </div>
                  <div className="mt-1 h-1 bg-white/20 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-sky-400 rounded-full"
                      style={{ width: `${getScoreValue(rideRating.punctuality) || 0}%` }}
                    />
                  </div>
                </div>

                {/* Ride Management */}
                <div className="bg-white/10 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Target className="h-3.5 w-3.5 text-violet-400" />
                    <span className="text-[10px] text-emerald-200 uppercase">Management</span>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-xl font-bold text-white">
                      {Math.round(getScoreValue(rideRating.ride_management) || 0)}
                    </span>
                    <span className="text-xs text-emerald-300">pts</span>
                  </div>
                  <div className="mt-1 h-1 bg-white/20 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-violet-400 rounded-full"
                      style={{ width: `${getScoreValue(rideRating.ride_management) || 0}%` }}
                    />
                  </div>
                </div>

                {/* Reliability */}
                <div className="bg-white/10 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Shield className="h-3.5 w-3.5 text-emerald-400" />
                    <span className="text-[10px] text-emerald-200 uppercase">Reliability</span>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-xl font-bold text-white">
                      {Math.round(getScoreValue(rideRating.reliability) || 0)}
                    </span>
                    <span className="text-xs text-emerald-300">pts</span>
                  </div>
                  <div className="mt-1 h-1 bg-white/20 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-emerald-400 rounded-full"
                      style={{ width: `${getScoreValue(rideRating.reliability) || 0}%` }}
                    />
                  </div>
                </div>

                {/* Quality */}
                <div className="bg-white/10 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Sparkles className="h-3.5 w-3.5 text-amber-400" />
                    <span className="text-[10px] text-emerald-200 uppercase">Quality</span>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-xl font-bold text-white">
                      {Math.round(getScoreValue(rideRating.service_quality) || 0)}
                    </span>
                    <span className="text-xs text-emerald-300">pts</span>
                  </div>
                  <div className="mt-1 h-1 bg-white/20 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-amber-400 rounded-full"
                      style={{ width: `${getScoreValue(rideRating.service_quality) || 0}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Feedback tip */}
              {ratingScore && ratingScore < 100 && (
                <div className="mt-3 text-center">
                  <p className="text-emerald-200 text-xs">
                    {ratingScore >= 90 ? 'Excellent work! Keep it up!' : 
                     ratingScore >= 80 ? 'Good job! Small improvements can boost your score.' :
                     'Review the details to improve your rating.'}
                  </p>
                </div>
              )}
            </div>
          ) : null}

          {/* Ride Summary */}
          <div className="w-full max-w-sm bg-white/10 backdrop-blur-sm rounded-2xl p-4 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-2.5 h-2.5 bg-emerald-400 rounded-full mt-1.5"></div>
              <div>
                <p className="text-emerald-200 text-xs uppercase tracking-wide">Pickup</p>
                <p className="text-white font-medium text-sm">{ride.pickup_location || 'N/A'}</p>
              </div>
            </div>
            
            <div className="border-l-2 border-dashed border-emerald-400/30 h-3 ml-1"></div>
            
            <div className="flex items-start gap-3">
              <div className="w-2.5 h-2.5 bg-amber-400 rounded-full mt-1.5"></div>
              <div>
                <p className="text-emerald-200 text-xs uppercase tracking-wide">Dropoff</p>
                <p className="text-white font-medium text-sm">{ride.dropoff_location || 'N/A'}</p>
              </div>
            </div>

            {ride.customer_name && (
              <div className="pt-3 border-t border-white/10">
                <p className="text-emerald-200 text-xs uppercase tracking-wide mb-1">Passenger</p>
                <p className="text-white font-medium text-sm">{ride.customer_name}</p>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Button */}
        <div className="p-4 pt-2">
          <Button
            onClick={() => navigate('/driver')}
            className="w-full h-12 bg-white text-emerald-700 hover:bg-emerald-50 rounded-2xl text-base font-semibold shadow-lg"
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // NO SHOW SCREEN
  if (ridePhase === 'no_show') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-600 to-red-800 flex flex-col">
        {/* Header */}
        <div className="p-4">
          <button onClick={() => navigate('/driver')} className="text-white/80 hover:text-white">
            <ArrowLeft className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-white">
          <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mb-6 shadow-2xl">
            <XCircle className="h-14 w-14 text-red-600" />
          </div>
          
          <h1 className="text-3xl font-bold mb-2">Passenger No Show</h1>
          <p className="text-red-100 mb-8">This ride has been marked as a no-show.</p>

          {/* Ride Summary */}
          <div className="w-full max-w-sm bg-white/10 backdrop-blur-sm rounded-2xl p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-3 h-3 bg-red-400 rounded-full mt-1.5"></div>
              <div>
                <p className="text-red-200 text-xs uppercase tracking-wide">Pickup Location</p>
                <p className="text-white font-medium">{ride.pickup_location || 'N/A'}</p>
                <p className="text-red-200 text-sm">{formatTime(ride.date_time)}</p>
              </div>
            </div>

            {ride.customer_name && (
              <div className="pt-4 border-t border-white/10">
                <p className="text-red-200 text-xs uppercase tracking-wide mb-1">Passenger</p>
                <p className="text-white font-medium">{ride.customer_name}</p>
              </div>
            )}

            {ride.no_show_notes && (
              <div className="pt-4 border-t border-white/10">
                <p className="text-red-200 text-xs uppercase tracking-wide mb-1">Notes</p>
                <p className="text-white text-sm">{ride.no_show_notes}</p>
              </div>
            )}

            {ride.no_show_proof_photos && ride.no_show_proof_photos.length > 0 && (
              <div className="pt-4 border-t border-white/10">
                <p className="text-red-200 text-xs uppercase tracking-wide mb-2">
                  Proof Photos ({ride.no_show_proof_photos.length})
                </p>
                <div className="flex items-center gap-2 text-white/80 text-sm">
                  <Camera className="h-4 w-4" />
                  <span>{ride.no_show_proof_photos.length} photo{ride.no_show_proof_photos.length > 1 ? 's' : ''} attached</span>
                </div>
              </div>
            )}
            
            {/* Legacy single photo support */}
            {!ride.no_show_proof_photos && ride.no_show_proof_photo && (
              <div className="pt-4 border-t border-white/10">
                <p className="text-red-200 text-xs uppercase tracking-wide mb-2">Proof Uploaded</p>
                <div className="flex items-center gap-2 text-white/80 text-sm">
                  <Camera className="h-4 w-4" />
                  <span>{ride.no_show_proof_filename || 'Image attached'}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Button */}
        <div className="p-6">
          <Button
            onClick={() => navigate('/driver')}
            className="w-full h-14 bg-white text-red-700 hover:bg-red-50 rounded-2xl text-lg font-semibold shadow-lg"
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // MAIN RIDE INTERFACE
  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <ActionStatusOverlay action={successOverlay} onDone={() => setSuccessOverlay(null)} />
      {/* Destination Bar (visible during navigation and in_progress) */}
      {(ridePhase === 'navigating' || ridePhase === 'in_progress') && (
        <div className="bg-blue-600 text-white px-4 py-3 flex items-center justify-between z-20">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <MapPin className="h-5 w-5 flex-shrink-0" />
            <p className="text-sm font-medium truncate">
              {ridePhase === 'in_progress' ? ride.dropoff_location : ride.pickup_location}
            </p>
          </div>
          <Button
            onClick={() => openGoogleMaps(ridePhase === 'in_progress' ? ride.dropoff_location : ride.pickup_location)}
            className="bg-white text-blue-600 hover:bg-blue-50 rounded-xl px-4 py-2 text-sm font-semibold flex-shrink-0"
          >
            <Navigation className="h-4 w-4 mr-1" />
            Navigate
          </Button>
        </div>
      )}

      {/* Map Area */}
      <div className="relative h-[45vh] min-h-[300px] bg-slate-300">
        {/* Back Button */}
        <button 
          onClick={() => navigate('/driver')}
          className="absolute top-4 left-4 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-slate-50"
        >
          <ArrowLeft className="h-5 w-5 text-slate-700" />
        </button>
        
        {/* GPS Status Indicator */}
        <div className="absolute top-4 right-4 z-10">
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full shadow-lg text-xs font-medium ${
            isTracking && currentLocation 
              ? 'bg-emerald-500 text-white' 
              : locationError 
                ? 'bg-red-500 text-white'
                : 'bg-slate-500 text-white'
          }`}>
            <Locate className={`h-3.5 w-3.5 ${isTracking ? 'animate-pulse' : ''}`} />
            {isTracking && currentLocation ? 'GPS Active' : locationError ? 'GPS Error' : 'GPS Off'}
          </div>
        </div>

        {/* Map Placeholder with gradient overlay */}
        <div className="w-full h-full bg-gradient-to-b from-slate-200 to-slate-300 flex items-center justify-center relative overflow-hidden">
          {/* Decorative map-like pattern */}
          <div className="absolute inset-0 opacity-30">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <path d="M0,20 Q25,40 50,20 T100,20" fill="none" stroke="#94a3b8" strokeWidth="0.5"/>
              <path d="M0,40 Q25,60 50,40 T100,40" fill="none" stroke="#94a3b8" strokeWidth="0.5"/>
              <path d="M0,60 Q25,80 50,60 T100,60" fill="none" stroke="#94a3b8" strokeWidth="0.5"/>
              <path d="M0,80 Q25,100 50,80 T100,80" fill="none" stroke="#94a3b8" strokeWidth="0.5"/>
              <path d="M20,0 Q40,25 20,50 T20,100" fill="none" stroke="#94a3b8" strokeWidth="0.5"/>
              <path d="M50,0 Q70,25 50,50 T50,100" fill="none" stroke="#94a3b8" strokeWidth="0.5"/>
              <path d="M80,0 Q100,25 80,50 T80,100" fill="none" stroke="#94a3b8" strokeWidth="0.5"/>
            </svg>
          </div>
          
          {/* Route visualization */}
          <div className="relative z-10 flex flex-col items-center">
            <div className="flex items-center gap-8">
              {/* Pickup marker */}
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg">
                  <MapPin className="h-6 w-6 text-white" />
                </div>
                <span className="text-xs font-medium text-slate-600 mt-2">Pickup</span>
              </div>
              
              {/* Route line */}
              <div className="flex items-center gap-1">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="w-2 h-2 bg-amber-500 rounded-full opacity-60"></div>
                ))}
              </div>
              
              {/* Dropoff marker */}
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center shadow-lg">
                  <MapPin className="h-6 w-6 text-white" />
                </div>
                <span className="text-xs font-medium text-slate-600 mt-2">Dropoff</span>
              </div>
            </div>
          </div>
        </div>

        {/* Status Badge */}
        <div className="absolute bottom-4 left-4 right-4 flex justify-center">
          <div className={`px-4 py-2 rounded-full text-sm font-medium shadow-lg ${
            ridePhase === 'departing' ? 'bg-amber-500 text-white' :
            ridePhase === 'navigating' ? 'bg-blue-500 text-white' :
            'bg-emerald-500 text-white'
          }`}>
            {ridePhase === 'departing' && '📍 Ready to depart'}
            {ridePhase === 'navigating' && '🚗 On the way to pickup'}
            {ridePhase === 'in_progress' && '👤 Passenger on board'}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 bg-white rounded-t-3xl -mt-6 z-10 flex flex-col">
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1.5 bg-slate-200 rounded-full"></div>
        </div>

        {/* Ride Info */}
        <div className="flex-1 px-6 pb-6 overflow-y-auto">
          {/* Time and Date */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-2xl font-bold text-slate-900">{formatTime(ride.date_time)}</p>
              <p className="text-slate-500 text-sm">{formatDate(ride.date_time)}</p>
            </div>
            {ride.vehicle_type && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-full">
                <Car className="h-4 w-4 text-slate-600" />
                <span className="text-sm font-medium text-slate-700">{ride.vehicle_type}</span>
              </div>
            )}
          </div>

          {/* Locations */}
          <div className="space-y-3 mb-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Pickup</p>
                <p className="text-slate-900 font-medium">{ride.pickup_location || 'Not specified'}</p>
              </div>
            </div>

            <div className="ml-5 border-l-2 border-dashed border-slate-200 h-4"></div>

            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Dropoff</p>
                <p className="text-slate-900 font-medium">{ride.dropoff_location || 'Not specified'}</p>
              </div>
            </div>
          </div>

          {/* Passenger Info (visible during navigation and in_progress) */}
          {(ridePhase === 'navigating' || ridePhase === 'in_progress') && (
            <div className="bg-slate-50 rounded-2xl p-4 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center">
                    <User className="h-6 w-6 text-slate-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{ride.customer_name || 'Passenger'}</p>
                    <div className="flex items-center gap-3 text-sm text-slate-500">
                      {ride.number_of_people && (
                        <span className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          {ride.number_of_people}
                        </span>
                      )}
                      {ride.luggage_count && (
                        <span className="flex items-center gap-1">
                          <Briefcase className="h-4 w-4" />
                          {ride.luggage_count}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Contact Buttons */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={callPassenger}
                    className="w-11 h-11 bg-amber-400 hover:bg-amber-500 rounded-full flex items-center justify-center text-white shadow-md transition-colors"
                    data-testid="call-passenger-btn"
                  >
                    <Phone className="h-5 w-5" />
                  </button>
                  <button
                    onClick={messagePassenger}
                    className="w-11 h-11 bg-blue-500 hover:bg-blue-600 rounded-full flex items-center justify-center text-white shadow-md transition-colors"
                    data-testid="sms-passenger-btn"
                  >
                    <MessageCircle className="h-5 w-5" />
                  </button>
                  <button
                    onClick={whatsappPassenger}
                    className="w-11 h-11 bg-[#25D366] hover:bg-[#1da851] rounded-full flex items-center justify-center text-white shadow-md transition-colors"
                    data-testid="whatsapp-passenger-btn"
                  >
                    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Flight Number */}
          {ride.flight_number && (
            <div className="flex items-center gap-2 mb-4 p-3 bg-blue-50 rounded-xl">
              <span className="text-blue-600 text-sm font-medium">✈️ Flight:</span>
              <span className="text-blue-800 font-semibold">{ride.flight_number}</span>
            </div>
          )}

          {/* Extras */}
          {ride.extras && ride.extras.length > 0 && (
            <div className="mb-4 p-3 bg-purple-50 rounded-xl" data-testid="ride-extras-info">
              <span className="text-purple-600 text-sm font-medium mr-2">Extra:</span>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {ride.extras.map((extra) => (
                  <span key={extra} className="bg-purple-100 text-purple-800 px-2.5 py-0.5 rounded-full text-xs font-medium">
                    {extra}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Flight Delay Info - show if already reported */}
          {ride.flight_delay_info && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="flight-delay-info">
              <div className="flex items-center gap-2 mb-2">
                <Plane className="h-4 w-4 text-amber-600" />
                <span className="text-amber-800 font-semibold text-sm">Flight Delay Reported</span>
              </div>
              <div className="space-y-1 text-sm text-amber-700">
                <p>New arrival: <span className="font-semibold">{new Date(ride.flight_delay_info.new_arrival_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span></p>
                {ride.flight_delay_info.notes && <p>Notes: {ride.flight_delay_info.notes}</p>}
                <p className="text-xs text-amber-500">Reported by {ride.flight_delay_info.reported_by_name}</p>
              </div>
            </div>
          )}

          {/* Report/Update Flight Delay Button - show during navigating/departing/in_progress phases for airport rides */}
          {(ridePhase === 'navigating' || ridePhase === 'departing' || ridePhase === 'in_progress') && (ride.is_airport_pickup || ride.flight_number) && (
            <div className="mb-4">
              <Button
                onClick={() => setShowFlightDelayModal(true)}
                variant="outline"
                className="w-full h-12 border-2 border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300 rounded-xl font-medium"
                data-testid="report-flight-delay-btn"
              >
                <Plane className="h-4 w-4 mr-2" />
                {ride.flight_delay_info ? 'Update Flight Time' : 'Report Flight Delay'}
              </Button>
              {ride.flight_delay_info && (
                <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                  <p className="text-xs font-semibold text-amber-700">Delay reported</p>
                  <p className="text-sm text-amber-900 mt-0.5">
                    Nuovo arrivo: {(() => { try { return new Date(ride.flight_delay_info.new_arrival_time).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }); } catch { return 'N/A'; } })()}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Nameboard Mode Hint - Show when arrived at pickup but not yet picked up */}
          {nameboardEnabled && (
            <div className="mb-4 p-4 bg-slate-900 rounded-xl" data-testid="nameboard-hint">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center flex-shrink-0">
                  <Smartphone className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium text-sm">Nameboard Mode Available</p>
                  <p className="text-slate-400 text-xs flex items-center gap-1">
                    <RotateCcw className="h-3 w-3" />
                    Rotate phone to landscape to display passenger name
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Expandable Instructions (departing phase) */}
          {ridePhase === 'departing' && ride.notes && (
            <div className="mb-4">
              <button
                onClick={() => setShowInstructions(!showInstructions)}
                className="w-full flex items-center justify-between p-4 bg-amber-50 rounded-xl text-left"
              >
                <span className="text-amber-800 font-medium">📝 Pickup Instructions</span>
                {showInstructions ? (
                  <ChevronUp className="h-5 w-5 text-amber-600" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-amber-600" />
                )}
              </button>
              {showInstructions && (
                <div className="mt-2 p-4 bg-amber-50 rounded-xl border-t border-amber-100">
                  <p className="text-amber-900 text-sm">{ride.notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Notes (in_progress phase) */}
          {ridePhase === 'in_progress' && ride.notes && (
            <div className="mb-4 p-3 bg-slate-50 rounded-xl">
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Notes</p>
              <p className="text-slate-700 text-sm">{ride.notes}</p>
            </div>
          )}
        </div>

        {/* Bottom Action Button */}
        <div className="p-6 pt-2 border-t border-slate-100">
          {ridePhase === 'departing' && (
            <Button
              onClick={startNavigation}
              className="w-full h-14 bg-amber-500 hover:bg-amber-600 text-white rounded-2xl text-lg font-semibold shadow-lg shadow-amber-500/30"
            >
              <Navigation className="h-5 w-5 mr-2" />
              Go to Pickup
            </Button>
          )}

          {ridePhase === 'navigating' && !ride.arrived_at_pickup && (
            <div className="space-y-2">
              <Button
                onClick={() => arrivedAtPickup()}
                disabled={pickupGeofenceStatus.isHardBlocked || actionInFlight === 'pickup'}
                className={`w-full h-14 rounded-2xl text-lg font-semibold shadow-lg ${
                  pickupGeofenceStatus.isHardBlocked || actionInFlight === 'pickup'
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-500 hover:bg-blue-600 shadow-blue-500/30'
                } text-white`}
                data-testid="arrived-pickup-btn"
              >
                {actionInFlight === 'pickup' ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <MapPin className="h-5 w-5 mr-2" />
                    I'm at the Pickup Point
                  </>
                )}
              </Button>
              {pickupGeofenceStatus.isHardBlocked && pickupGeofenceStatus.distance && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-300 rounded-xl text-red-700 text-sm">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span>You can't select Pick up because you are too far from the pickup location ({(pickupGeofenceStatus.distance / 1000).toFixed(1)}km away). Move closer to continue.</span>
                </div>
              )}
              {!pickupGeofenceStatus.isHardBlocked && !pickupGeofenceStatus.isWithinSoftGeofence && pickupGeofenceStatus.distance && (
                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-sm">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span>Note: You are {pickupGeofenceStatus.distance}m away from pickup location.</span>
                </div>
              )}
            </div>
          )}

          {ridePhase === 'navigating' && ride.arrived_at_pickup && !ride.picked_up_customer && (
            <div className="space-y-3">
              <Button
                onClick={() => pickedUpCustomer()}
                disabled={pickupGeofenceStatus.isHardBlocked || actionInFlight === 'picked-up'}
                className={`w-full h-14 rounded-2xl text-lg font-semibold shadow-lg ${
                  pickupGeofenceStatus.isHardBlocked || actionInFlight === 'picked-up'
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-emerald-500 hover:bg-emerald-600 shadow-emerald-500/30'
                } text-white`}
                data-testid="passenger-picked-up-btn"
              >
                {actionInFlight === 'picked-up' ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <User className="h-5 w-5 mr-2" />
                    Passenger Picked Up
                  </>
                )}
              </Button>
              {pickupGeofenceStatus.isHardBlocked && pickupGeofenceStatus.distance && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-300 rounded-xl text-red-700 text-sm">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span>You can't select Pick up because you are too far from the pickup location ({(pickupGeofenceStatus.distance / 1000).toFixed(1)}km away). Move closer to continue.</span>
                </div>
              )}
              {!pickupGeofenceStatus.isHardBlocked && !pickupGeofenceStatus.isWithinSoftGeofence && pickupGeofenceStatus.distance && (
                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-sm">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span>Note: You are {pickupGeofenceStatus.distance}m away from pickup location.</span>
                </div>
              )}
              <Button
                onClick={() => setShowNoShowModal(true)}
                variant="outline"
                className="w-full h-14 border-2 border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 rounded-2xl text-lg font-semibold"
                data-testid="passenger-no-show-btn"
              >
                <XCircle className="h-5 w-5 mr-2" />
                Passenger No Show
              </Button>
            </div>
          )}

          {ridePhase === 'in_progress' && (
            <Button
              onClick={() => completeRide()}
              disabled={actionInFlight === 'dropoff'}
              className={`w-full h-14 text-white rounded-2xl text-lg font-semibold shadow-lg ${
                actionInFlight === 'dropoff'
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-emerald-500 hover:bg-emerald-600 shadow-emerald-500/30'
              }`}
              data-testid="complete-ride-btn"
            >
              {actionInFlight === 'dropoff' ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Complete Ride
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* No Show Modal */}
      <Dialog open={showNoShowModal} onOpenChange={resetNoShowModal}>
        <DialogContent className="sm:max-w-md mx-4 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Report No Show
            </DialogTitle>
            <DialogDescription>
              Upload proof photos that the passenger did not show up (up to 15 photos).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Upload Area */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 flex items-center justify-between">
                <span>Proof Photos <span className="text-red-500">*</span></span>
                <span className="text-xs text-slate-400">{noShowPhotos.length}/15 photos</span>
              </label>
              
              {/* Photo Grid */}
              {noShowPhotos.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {noShowPhotos.map((photo) => (
                    <div key={photo.id} className="relative aspect-square">
                      <img
                        src={photo.preview}
                        alt={photo.filename}
                        className="w-full h-full object-cover rounded-lg border border-slate-200"
                      />
                      <button
                        onClick={() => removeNoShowPhoto(photo.id)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add More Photos Button */}
              {noShowPhotos.length < 15 && (
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer bg-slate-50 hover:bg-slate-100 transition-colors">
                  <div className="flex flex-col items-center justify-center py-4">
                    <Camera className="h-8 w-8 text-slate-400 mb-2" />
                    <p className="text-sm text-slate-500">
                      <span className="font-semibold text-slate-700">
                        {noShowPhotos.length === 0 ? 'Click to upload' : 'Add more photos'}
                      </span>
                    </p>
                    <p className="text-xs text-slate-400">PNG, JPG or JPEG (max 10MB each)</p>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleNoShowProofSelect}
                    className="hidden"
                    data-testid="no-show-proof-input"
                  />
                </label>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Additional Notes (optional)
              </label>
              <textarea
                value={noShowNotes}
                onChange={(e) => setNoShowNotes(e.target.value)}
                placeholder="E.g., Waited 15 minutes, no response to calls..."
                className="w-full h-20 px-3 py-2 border border-slate-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                data-testid="no-show-notes-input"
              />
            </div>

            {/* Warning */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-amber-800 text-sm flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>This action will mark the ride as "No Show" and notify the supervisor. Please ensure you have valid proof before submitting.</span>
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={resetNoShowModal}
              className="flex-1 h-12 rounded-xl"
              disabled={submittingNoShow}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitNoShow}
              disabled={noShowPhotos.length === 0 || submittingNoShow}
              className="flex-1 h-12 bg-red-500 hover:bg-red-600 text-white rounded-xl disabled:opacity-50"
              data-testid="submit-no-show-btn"
            >
              {submittingNoShow ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Submitting...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Confirm No Show
                </span>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Flight Delay Modal */}
      <Dialog open={showFlightDelayModal} onOpenChange={resetFlightDelayModal}>
        <DialogContent className="sm:max-w-md mx-4 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <Plane className="h-5 w-5" />
              Report Flight Delay
            </DialogTitle>
            <DialogDescription>
              Report a delayed flight so the punctuality rating accounts for the new arrival time.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* New Arrival Time */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                New Arrival Time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={flightDelayTime}
                onChange={(e) => setFlightDelayTime(e.target.value)}
                className="w-full h-12 px-4 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                data-testid="flight-delay-time-input"
              />
              <p className="text-xs text-slate-400">Enter the updated flight arrival time</p>
            </div>

            {/* Proof Upload (Optional) */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Proof Screenshot (optional)
              </label>
              {flightDelayProof ? (
                <div className="relative">
                  <img
                    src={flightDelayProof.preview}
                    alt="Flight delay proof"
                    className="w-full h-40 object-cover rounded-xl border border-slate-200"
                  />
                  <button
                    onClick={() => setFlightDelayProof(null)}
                    className="absolute top-2 right-2 w-7 h-7 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <p className="text-xs text-slate-500 mt-1">{flightDelayProof.filename}</p>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer bg-slate-50 hover:bg-slate-100 transition-colors">
                  <div className="flex flex-col items-center justify-center py-3">
                    <Camera className="h-7 w-7 text-slate-400 mb-1" />
                    <p className="text-sm text-slate-500">
                      <span className="font-semibold text-slate-700">Upload screenshot</span>
                    </p>
                    <p className="text-xs text-slate-400">Flight tracker, airline app, etc.</p>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFlightDelayProofSelect}
                    className="hidden"
                    data-testid="flight-delay-proof-input"
                  />
                </label>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Notes (optional)</label>
              <textarea
                value={flightDelayNotes}
                onChange={(e) => setFlightDelayNotes(e.target.value)}
                placeholder="E.g., Flight from London delayed 45 minutes..."
                className="w-full h-20 px-3 py-2 border border-slate-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                data-testid="flight-delay-notes-input"
              />
            </div>

            {/* Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
              <p className="text-blue-800 text-sm flex items-start gap-2">
                <Plane className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>Airport pickups have a 30-minute tolerance. Reporting a flight delay adjusts the reference time for your punctuality rating.</span>
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={resetFlightDelayModal}
              className="flex-1 h-12 rounded-xl"
              disabled={submittingFlightDelay}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitFlightDelay}
              disabled={!flightDelayTime || submittingFlightDelay}
              className="flex-1 h-12 bg-blue-500 hover:bg-blue-600 text-white rounded-xl disabled:opacity-50"
              data-testid="submit-flight-delay-btn"
            >
              {submittingFlightDelay ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Submitting...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Report Delay
                </span>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Geofence Warning Dialog */}
      <Dialog open={showGeofenceWarning} onOpenChange={(open) => {
        setShowGeofenceWarning(open);
        if (!open) setPendingAction(null);
      }}>
        <DialogContent className="sm:max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <MapPinned className="h-5 w-5" />
              Location Warning
            </DialogTitle>
            <DialogDescription>
              {geofenceWarningMessage}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-amber-800 text-sm flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>You are outside the expected geofence area. You can still proceed, but this will be logged for admin review.</span>
              </p>
            </div>
            
            {currentLocation && (
              <div className="mt-4 p-3 bg-slate-50 rounded-xl">
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Your Current Location</p>
                <p className="text-slate-700 text-sm font-mono">
                  {currentLocation.latitude.toFixed(6)}, {currentLocation.longitude.toFixed(6)}
                </p>
                {currentLocation.accuracy && (
                  <p className="text-xs text-slate-400 mt-1">Accuracy: ±{Math.round(currentLocation.accuracy)}m</p>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowGeofenceWarning(false);
                setPendingAction(null);
              }}
              className="flex-1 h-12 rounded-xl"
            >
              Cancel
            </Button>
            <Button
              onClick={handleGeofenceConfirm}
              className="flex-1 h-12 bg-amber-500 hover:bg-amber-600 text-white rounded-xl"
            >
              Proceed Anyway
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dropoff Reminder Popup - Triggered when driver moves away from dropoff without completing */}
      <Dialog open={showDropoffReminder} onOpenChange={setShowDropoffReminder}>
        <DialogContent className="sm:max-w-md mx-4 border-2 border-red-500">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600 text-xl">
              <AlertTriangle className="h-7 w-7 animate-pulse" />
              DROPOFF REMINDER!
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-center">
              <div className="h-24 w-24 bg-red-100 rounded-full flex items-center justify-center animate-bounce border-4 border-red-500">
                <MapPin className="h-12 w-12 text-red-600" />
              </div>
            </div>
            
            <div className="text-center space-y-2">
              <p className="text-xl font-bold text-red-700">
                Did you forget to complete the dropoff?
              </p>
              <p className="text-slate-600 text-base">
                You passed the dropoff location and are now moving away. Please complete the ride if the customer has been dropped off.
              </p>
              {dropoffGeofenceStatus.distance && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
                  <p className="text-base text-red-700 font-semibold">
                    You are currently {(dropoffGeofenceStatus.distance / 1000).toFixed(1)}km from the dropoff point!
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowDropoffReminder(false)}
                className="flex-1 h-14 rounded-xl text-base"
              >
                Dismiss
              </Button>
              <Button
                onClick={() => {
                  setShowDropoffReminder(false);
                  completeRide(true); // Force complete
                }}
                className="flex-1 h-14 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-base font-semibold"
              >
                <CheckCircle className="h-5 w-5 mr-2" />
                Complete Ride
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* GPS Instructions Modal - Visual step-by-step guide */}
      <Dialog open={showInstructionsModal} onOpenChange={setShowInstructionsModal}>
        <DialogContent className="sm:max-w-md mx-4 max-h-[85vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-800">
              <Settings className="h-5 w-5 text-amber-500" />
              Enable GPS Location
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* Platform-specific instructions */}
            {detectedPlatform === 'ios' && (
              <div className="space-y-4">
                <div className="bg-slate-100 rounded-lg p-3">
                  <p className="text-sm font-medium text-slate-700 mb-1">Detected: iPhone/iPad</p>
                </div>
                
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
                    <div>
                      <p className="font-medium text-slate-800">Open Settings app</p>
                      <p className="text-sm text-slate-600">Go to your iPhone Settings</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
                    <div>
                      <p className="font-medium text-slate-800">Privacy & Security → Location Services</p>
                      <p className="text-sm text-slate-600">Turn ON Location Services</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
                    <div>
                      <p className="font-medium text-slate-800">Find Safari (or your browser)</p>
                      <p className="text-sm text-slate-600">Set to "While Using the App"</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center font-bold text-sm">4</div>
                    <div>
                      <p className="font-medium text-slate-800">Return here and tap "Try Again"</p>
                      <p className="text-sm text-slate-600">The app will request permission again</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {detectedPlatform === 'android' && (
              <div className="space-y-4">
                <div className="bg-slate-100 rounded-lg p-3">
                  <p className="text-sm font-medium text-slate-700 mb-1">Detected: Android</p>
                </div>
                
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
                    <div>
                      <p className="font-medium text-slate-800">Open Settings app</p>
                      <p className="text-sm text-slate-600">Go to your phone Settings</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
                    <div>
                      <p className="font-medium text-slate-800">Location</p>
                      <p className="text-sm text-slate-600">Turn ON "Use location"</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
                    <div>
                      <p className="font-medium text-slate-800">In Chrome: tap ⋮ → Settings → Site settings → Location</p>
                      <p className="text-sm text-slate-600">Make sure Location is set to "Ask first" or "Allowed"</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center font-bold text-sm">4</div>
                    <div>
                      <p className="font-medium text-slate-800">Return here and tap "Try Again"</p>
                      <p className="text-sm text-slate-600">The app will request permission again</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {detectedPlatform === 'desktop' && (
              <div className="space-y-4">
                <div className="bg-slate-100 rounded-lg p-3">
                  <p className="text-sm font-medium text-slate-700 mb-1">Desktop Browser</p>
                </div>
                
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
                    <div>
                      <p className="font-medium text-slate-800">Click the lock icon 🔒 in address bar</p>
                      <p className="text-sm text-slate-600">It's at the left side of the URL</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
                    <div>
                      <p className="font-medium text-slate-800">Find "Location" in permissions</p>
                      <p className="text-sm text-slate-600">Change from "Block" to "Allow"</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
                    <div>
                      <p className="font-medium text-slate-800">Click "Try Again" below</p>
                      <p className="text-sm text-slate-600">The app will request permission again</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="pt-2 space-y-2">
              <Button
                onClick={() => {
                  setShowInstructionsModal(false);
                  handleRetryPermission();
                }}
                className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                I've Done It - Try Again
              </Button>
              <Button
                onClick={() => setShowInstructionsModal(false)}
                variant="outline"
                className="w-full h-10 rounded-xl"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
